import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CommissionGroupResponse, CommissionGroupId, CommissionGroupRequest } from '@business/entities/commission_group';
import { CommissionGroupAPI } from '@business/api/commission_group';

export class CommissionGroupBloc extends BaseBloc {
  private readonly commissionGroupsRepo = new Repository<CommissionGroupResponse>({
    getItemId: group => group.id,
  });

  readonly commissionGroups$ = this.commissionGroupsRepo.items$;
  readonly selectCommissionGroups = this.commissionGroupsRepo.selectItem;

  onReset() {
    this.commissionGroupsRepo.reset();
  }

  fetchCommissionGroups = () => {
    return CommissionGroupAPI.fetchCommissionGroups().pipe(
      this.commissionGroupsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_commission_groups_error') }),
    );
  };

  fetchCommissionGroup = (id: CommissionGroupId) => {
    return CommissionGroupAPI.fetchCommissionGroup(id).pipe(
      this.commissionGroupsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_commission_groups_error') }),
    );
  };

  createCommissionGroup = (commissionGroup: Partial<CommissionGroupRequest>) => {
    return CommissionGroupAPI.createCommissionGroup(commissionGroup).pipe(
      this.commissionGroupsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_commission_group'),
      }),
    );
  };

  updateCommissionGroup = (id: CommissionGroupId, commissionGroup: Partial<CommissionGroupRequest>) => {
    return CommissionGroupAPI.updateCommissionGroup(id, commissionGroup).pipe(
      this.commissionGroupsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_commission_group'),
      }),
    );
  };

  deleteCommissionGroup = (id: CommissionGroupId) => {
    return showDeleteConfirmation('Delete CommissionGroups', 'Do you really want to remove this commissionGroup?').pipe(
      switchMap(() => CommissionGroupAPI.deleteCommissionGroup(id)),
      this.commissionGroupsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_commission_group'),
      }),
    );
  };
}
